/**
 * Shared types for moonshot fixture data.
 */

/** A single SSE-style streaming chunk (for chat/assistant replies, deep research). */
export interface StreamChunk {
  /** Delta text to append to the assistant message for this tick. */
  delta: string;
  /** Milliseconds to wait BEFORE emitting this chunk. Defaults to 40ms. */
  delayMs?: number;
  /** Optional event type (default: "delta"). Other values: "done", "tool", "citation". */
  event?: string;
  /** Optional structured payload (e.g., citation data, tool call). */
  data?: Record<string, unknown>;
}

export interface ChatFixture {
  /** Fixture id. */
  id: string;
  /** The user prompt text for this journey. */
  userQuery: string;
  /** Stream chunks that compose the assistant reply. */
  chunks: StreamChunk[];
  /** Optional pre-recorded TTS audio asset path (played via the Volume2 button). */
  voiceReplyUrl?: string;
  /** Labelled steps for HUD progression (step X/Y). */
  steps: string[];
}

export interface FixtureResponder {
  /** Match function for this responder (returns true to claim the request). */
  match: (url: URL, method: string) => boolean;
  /** Produce a Response object for the matched request. */
  respond: (req: Request, url: URL) => Promise<Response>;
}

/** Build an SSE (text/event-stream) Response body from a list of stream chunks. */
export function streamChunksToSSE(chunks: StreamChunk[]): ReadableStream<Uint8Array> {
  const encoder = new TextEncoder();
  let cancelled = false;
  return new ReadableStream<Uint8Array>({
    async start(controller) {
      for (const chunk of chunks) {
        if (cancelled) break;
        const delay = chunk.delayMs ?? 40;
        if (delay > 0) await new Promise((r) => setTimeout(r, delay));
        const event = chunk.event || 'delta';
        const payload = chunk.event === 'delta' || !chunk.event
          ? { delta: chunk.delta }
          : { ...(chunk.data || {}), delta: chunk.delta };
        const line = `event: ${event}\ndata: ${JSON.stringify(payload)}\n\n`;
        controller.enqueue(encoder.encode(line));
      }
      if (!cancelled) {
        controller.enqueue(encoder.encode('event: done\ndata: {"ok":true}\n\n'));
        controller.close();
      }
    },
    cancel() {
      cancelled = true;
    },
  });
}

/** Build a plain-text streaming body (one delta per chunk, no SSE framing). */
export function streamChunksToText(chunks: StreamChunk[]): ReadableStream<Uint8Array> {
  const encoder = new TextEncoder();
  let cancelled = false;
  return new ReadableStream<Uint8Array>({
    async start(controller) {
      for (const chunk of chunks) {
        if (cancelled) break;
        const delay = chunk.delayMs ?? 40;
        if (delay > 0) await new Promise((r) => setTimeout(r, delay));
        controller.enqueue(encoder.encode(chunk.delta));
      }
      if (!cancelled) controller.close();
    },
    cancel() {
      cancelled = true;
    },
  });
}

/** Helper: tokenize a string into word-sized chunks with gentle pacing. */
export function tokenize(text: string, msPerWord = 45): StreamChunk[] {
  const words = text.split(/(\s+)/).filter((s) => s.length > 0);
  return words.map((w) => ({ delta: w, delayMs: msPerWord }));
}
